import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { intercompanySelectors, isCreditedIntercompanyRow } from 'store/reducers/intercompanySlice';
import {
    misPickSkuKey,
    returnSelectors,
    returnThunks,
    selectAllM3ItemsHaveLpn,
    setBatchHandled,
} from 'store/reducers/returnSlice';
import { ReturnItemRowStatus, ReturnOrderLineType, ReturnSlip, ReturnStatusType } from 'returnCenter-api';
import { selectOrigin, setOrigin, setSelectedStatus } from 'store/reducers/navigationSlice';
import { findMisPickLineWithoutParentQuantity, findMisPickParent, getStatusTypeString } from 'utils/helpers';
import { configSelectors } from 'store/reducers/configSlice';
import { isBatchAssignable } from 'hooks/useAutoApproveBatchHandling';

interface UseReturnSlipActionsOptions {
    returnSlip: ReturnSlip | null | undefined;
    returnSlipId: number | null | undefined;
    orderIdFromPath: string | undefined;
    returnStatus: ReturnStatusType | undefined;
}

export const useReturnSlipActions = ({
    returnSlip,
    returnSlipId,
    orderIdFromPath,
    returnStatus,
}: UseReturnSlipActionsOptions) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const origin = useAppSelector(selectOrigin);
    const latestReturnSlip = useAppSelector(returnSelectors.selectReturnSlip);
    const allItemsHaveReasonCodes = useAppSelector(returnSelectors.selectValidityCheckForReasonCode);
    const allItemsHaveDecisionCodes = useAppSelector(returnSelectors.selectValidityCheckForDecisionCode);
    const selectedReturnItemsHaveQty = useAppSelector(returnSelectors.selectIsReturnItemsQtyValid);
    const eachOrderHasReturnItemsWithQty = useAppSelector(returnSelectors.selectHasEachOrderAtLeastOneItemWithQty);
    const isRequestedReturn = useAppSelector(returnSelectors.selectIsRequestedReturn);
    const intercompanyProblems = useAppSelector(intercompanySelectors.selectProblems);
    const isM3Company = useAppSelector(configSelectors.selectIsM3Company);
    const misPickSkuChecks = useAppSelector(returnSelectors.selectMisPickSkuChecks);
    const allM3ItemsHaveLpn = useAppSelector(selectAllM3ItemsHaveLpn);
    const completesWithoutCredit = useAppSelector(returnSelectors.selectCompletesWithoutCredit);
    const firstRowWithoutBatch = useAppSelector(returnSelectors.selectFirstRowWithoutBatch);

    const navigateToReturnSlip = useCallback(
        (slipId: number, status: ReturnStatusType) => {
            const statusKey = getStatusTypeString(status);
            dispatch(setSelectedStatus(status));
            dispatch(setOrigin(origin ?? null));
            navigate(`/returns/${statusKey}/${slipId}`, {
                state: {
                    origin: origin ?? null,
                    returnStatus: status,
                },
            });
        },
        [navigate, dispatch, origin],
    );

    const updateReturnSlip = useCallback(
        (status?: ReturnStatusType) => {
            if (!latestReturnSlip) return;
            const updatedSlip = { ...latestReturnSlip, status: status ?? latestReturnSlip.status };
            dispatch(
                returnThunks.updateReturnSlip({
                    slip: updatedSlip,
                }),
            ).then((action) => {
                if (returnThunks.updateReturnSlip.fulfilled.match(action)) {
                    const updatedSlip = action.payload;
                    if (!updatedSlip?.id) return;

                    navigateToReturnSlip(updatedSlip.id, updatedSlip.status ?? status ?? ReturnStatusType.InProgress);
                }
            });
        },
        [dispatch, latestReturnSlip, navigateToReturnSlip],
    );

    const createReturnSlip = useCallback(
        (status?: ReturnStatusType) => {
            if (!returnSlip) return;

            return dispatch(
                returnThunks.createReturnSlip({
                    slip: returnSlip,
                    returnStatus: status,
                }),
            ).then((action) => {
                if (returnThunks.createReturnSlip.fulfilled.match(action)) {
                    const createdSlip = action.payload;
                    if (!createdSlip?.id) return;

                    navigateToReturnSlip(createdSlip.id, ReturnStatusType.InProgress);
                }
            });
        },
        [dispatch, returnSlip, navigateToReturnSlip],
    );

    // Saving drops an ordered line with nothing in the return, which would leave its mis-pick line without the line
    // it is credited against. Checked when saving here as well as on completing, so that state is not stored.
    const blockMisPickWithoutParentQuantity = useCallback((): boolean => {
        const found = findMisPickLineWithoutParentQuantity(returnSlip);
        if (!found) return false;
        toast.error(
            t('misPick.parentWithoutQuantity', {
                defaultValue:
                    'A mis-picked item needs the ordered item ({{parent}}) to have a quantity. Enter a quantity or remove the mis-picked item.',
                // Not the mis-pick line's own SKU: it is often still the ordered one, or blank.
                parent: found.parent?.sku ?? found.line.misPickParentOrderLineNumber ?? '',
            }),
            { autoClose: 5000, closeOnClick: true },
        );
        return true;
    }, [returnSlip, t]);

    const handleSaveWithStatus = useCallback(
        (status: ReturnStatusType) => {
            if (!returnSlip) return;

            if (!allItemsHaveReasonCodes) {
                toast.error(t('allItemsMustHaveReasonCodes'), {
                    autoClose: 3500,
                    closeOnClick: true,
                });
                return;
            }

            if (!eachOrderHasReturnItemsWithQty) {
                toast.error(t('eachOrderMustHaveAtLeastOneItemWithQty'), { autoClose: 3500, closeOnClick: true });
                return;
            }

            if (!selectedReturnItemsHaveQty) {
                toast.error(t('invalidQuantityInReturn'), { autoClose: 3500, closeOnClick: true });
                return;
            }

            if (blockMisPickWithoutParentQuantity()) return;

            if (returnSlipId) {
                updateReturnSlip(status);
                return;
            }

            createReturnSlip(status);
        },
        [
            returnSlip,
            returnSlipId,
            orderIdFromPath,
            allItemsHaveReasonCodes,
            selectedReturnItemsHaveQty,
            eachOrderHasReturnItemsWithQty,
            t,
            blockMisPickWithoutParentQuantity,
            createReturnSlip,
            updateReturnSlip,
        ],
    );

    // Validation function for complete return - call this before showing dialog
    const validateCompleteReturn = useCallback((): boolean => {
        if (!allItemsHaveReasonCodes) {
            toast.error(t('allItemsMustHaveReasonCodes'), {
                autoClose: 3500,
                closeOnClick: true,
            });
            return false;
        }

        if (isM3Company && !allM3ItemsHaveLpn) {
            toast.error(t('allItemsMustHaveLpn', 'All returned items must have an LPN.'), {
                autoClose: 3500,
                closeOnClick: true,
            });
            return false;
        }

        if (!eachOrderHasReturnItemsWithQty) {
            toast.error(t('eachOrderMustHaveAtLeastOneItemWithQty'), { autoClose: 3500, closeOnClick: true });
            return false;
        }

        // Without a credit, the batch is all the receiving company has to go on.
        if (completesWithoutCredit && firstRowWithoutBatch) {
            toast.error(
                firstRowWithoutBatch.hasDestination
                    ? t(
                          'batchSending.allRowsNeedABatch',
                          'Every returned row must be in a batch before the return can be completed.',
                      )
                    : t(
                          'batchSending.noDestination',
                          '{{sku}} has no batch destination. Remove the row or configure a batch destination for it.',
                          { sku: firstRowWithoutBatch.sku },
                      ),
                { autoClose: 6000, closeOnClick: true },
            );
            return false;
        }

        if (!completesWithoutCredit && !allItemsHaveDecisionCodes) {
            toast.error(t('allItemsMustHaveDecisionCodes'), {
                autoClose: 3500,
                closeOnClick: true,
            });
            return false;
        }

        if (blockMisPickWithoutParentQuantity()) return false;

        // A mis-pick line is prefilled with the ordered SKU and must be changed to the one actually delivered.
        // The server rejects it too, but this names the SKU before anything is sent.
        for (const group of returnSlip?.returnItems ?? []) {
            for (const line of group.orderLines ?? []) {
                if (line.type !== ReturnOrderLineType.MisPick) continue;
                // With nothing in the return it is dropped on save, so its SKU does not matter.
                const quantity = (line.returnItems ?? []).reduce((sum, item) => sum + (item.quantityInReturn ?? 0), 0);
                if (quantity <= 0) continue;
                // Returned without its ordered line is blocked above.
                const parent = findMisPickParent(line, group.orderLines);
                if (!parent) continue;
                const sku = parent.sku ?? '';
                const misPickSku = (line.sku ?? '').trim();
                if (!misPickSku) {
                    toast.error(t('misPick.blankSku', { defaultValue: 'Enter a SKU for the mis-picked item.' }), {
                        autoClose: 5000,
                        closeOnClick: true,
                    });
                    return false;
                }
                if (misPickSku.toLowerCase() === sku.trim().toLowerCase()) {
                    toast.error(
                        t('misPick.sameSku', {
                            defaultValue: 'A mis-picked item cannot have the same SKU as the ordered item ({{sku}}).',
                            sku,
                        }),
                        { autoClose: 5000, closeOnClick: true },
                    );
                    return false;
                }
                // Not checked yet, or the check failed: left to the server, which makes the same check.
                const check = misPickSkuChecks[misPickSkuKey(misPickSku)];
                if (check?.status === 'checking') {
                    toast.info(
                        t('misPick.skuChecking', {
                            defaultValue: 'Checking {{sku}} against the catalog. Try again in a moment.',
                            sku: misPickSku,
                        }),
                        { autoClose: 3500, closeOnClick: true },
                    );
                    return false;
                }
                if (check?.status === 'notFound') {
                    toast.error(
                        t('misPick.skuNotInCatalog', {
                            defaultValue: 'The mis-picked item {{sku}} is not in the catalog. Check its SKU.',
                            sku: misPickSku,
                        }),
                        { autoClose: 5000, closeOnClick: true },
                    );
                    return false;
                }
            }
        }

        // Undecided buy-out rows: the server rejects these too, but fail early with the SKUs named.
        if (returnSlip && !completesWithoutCredit) {
            const undecided = (returnSlip.returnItems ?? [])
                .flatMap((group) => group.orderLines ?? [])
                .filter((line) => line.isBuyOutEligible)
                .flatMap((line) => (line.returnItems ?? []).map((item) => ({ line, item })))
                .filter(
                    ({ item }) =>
                        (item.quantityInReturn ?? 0) > 0 &&
                        item.buyOutDecision == null &&
                        !item.intercompanyReturnId &&
                        !item.batchReturnId,
                );

            if (undecided.length > 0) {
                const skus = [...new Set(undecided.map(({ line }) => line.sku))].join(', ');
                toast.error(
                    t(
                        'buyOut.mustDecide',
                        'Decide whether these are bought or left alone before completing: {{skus}}',
                        { skus },
                    ),
                    { autoClose: 5000, closeOnClick: true },
                );
                return false;
            }
        }

        // Requested-quantity return: all rows with quantityRequested must be approved
        if (isRequestedReturn && returnSlip) {
            const hasPendingRows = returnSlip.returnItems
                ?.flatMap((group) => group.orderLines ?? [])
                .flatMap((ol) => ol.returnItems ?? [])
                .some((item) => (item.quantityRequested ?? 0) > 0 && item.status !== ReturnItemRowStatus.Approved);

            if (hasPendingRows) {
                toast.error(t('allRowsMustBeApproved'), {
                    autoClose: 3500,
                    closeOnClick: true,
                });
                return false;
            }
        }

        // Nothing below applies: completing creates no credit, so no intercompany return either.
        if (completesWithoutCredit) return true;

        const uncheckedIntercompanyRows = (returnSlip?.returnItems ?? [])
            .flatMap((group) => group.orderLines ?? [])
            .filter((line) => line.isIntercompanyEligible && line.supplyingCompany)
            .flatMap((line) => line.returnItems ?? [])
            .filter((item) => item.id == null && isCreditedIntercompanyRow(item));

        if (uncheckedIntercompanyRows.length > 0) {
            toast.error(
                t('intercompany.saveBeforeCompleting', {
                    count: uncheckedIntercompanyRows.length,
                    defaultValue:
                        'Save the return before completing: {{count}} intercompany line(s) have not been checked against the supplying company yet.',
                }),
                { autoClose: 6000, closeOnClick: true },
            );
            return false;
        }

        // An unlocked intercompany row is the operator's to decide, so leaving it empty is an
        // unfinished decision. Without this the panel would say "you choose" and then the supplying
        // company's rules would choose anyway, which is the one outcome nobody asked for.
        const undecidedIntercompanyRows = (returnSlip?.returnItems ?? [])
            .flatMap((group) => group.orderLines ?? [])
            .filter((line) => line.isIntercompanyEligible)
            .flatMap((line) => line.returnItems ?? [])
            .filter(
                (item) =>
                    (item.quantityInReturn ?? 0) > 0 &&
                    !item.batchReturnId &&
                    !item.intercompanyReturnId &&
                    item.intercompanyDecisionCodeIsManual &&
                    item.intercompanyDecisionCode == null,
            );

        if (undecidedIntercompanyRows.length > 0) {
            toast.error(
                t('intercompany.pickADecisionCode', {
                    count: undecidedIntercompanyRows.length,
                    defaultValue:
                        'Cannot complete: {{count}} unlocked intercompany line(s) have no decision code chosen.',
                }),
                { autoClose: 6000, closeOnClick: true },
            );
            return false;
        }

        // Completing also credits the supplying companies. A line they cannot credit would fail
        // after the customer is already credited, leaving someone to find it in the return log —
        // so stop here instead, while it can still be fixed.
        if (intercompanyProblems.length > 0) {
            const first = intercompanyProblems[0];
            toast.error(
                t('intercompany.cannotComplete', {
                    company: first.supplyingCompany,
                    sku: first.sku,
                    count: intercompanyProblems.length,
                    defaultValue:
                        'Cannot complete: {{count}} intercompany line(s) cannot be credited by {{company}}, starting with {{sku}}.',
                }),
                { autoClose: 6000, closeOnClick: true },
            );
            return false;
        }

        return true;
    }, [
        allItemsHaveReasonCodes,
        allItemsHaveDecisionCodes,
        allM3ItemsHaveLpn,
        blockMisPickWithoutParentQuantity,
        eachOrderHasReturnItemsWithQty,
        firstRowWithoutBatch,
        intercompanyProblems,
        completesWithoutCredit,
        isM3Company,
        isRequestedReturn,
        misPickSkuChecks,
        returnSlip,
        t,
    ]);

    const handleCompleteReturnSlip = useCallback(() => {
        if (!returnSlip) return;

        if (!validateCompleteReturn()) {
            return;
        }

        const returnIdSlip = returnSlip?.id;

        dispatch(
            returnThunks.createReturnCredit({
                slip: returnSlip,
                returnId: returnIdSlip,
                returnStatus,
            }),
        ).then((action) => {
            if (returnThunks.createReturnCredit.fulfilled.match(action)) {
                const createdSlip = action.payload;
                if (!createdSlip?.id) return;
                navigateToReturnSlip(createdSlip.id, createdSlip.status ?? ReturnStatusType.Completed);
            }
        });
    }, [dispatch, navigateToReturnSlip, returnSlip, returnStatus, validateCompleteReturn]);

    const handleCompleteBatch = useCallback(() => {
        if (!latestReturnSlip) return;
        const updatedSlip = { ...latestReturnSlip, batchHandled: true, status: ReturnStatusType.Completed };
        dispatch(
            returnThunks.updateReturnSlip({
                slip: updatedSlip,
            }),
        ).then((action) => {
            if (returnThunks.updateReturnSlip.fulfilled.match(action)) {
                const result = action.payload;
                if (!result?.id) return;
                navigateToReturnSlip(result.id, result.status ?? ReturnStatusType.Completed);
            }
        });
    }, [dispatch, latestReturnSlip, navigateToReturnSlip]);

    const handleReopenBatchSlip = useCallback(() => {
        if (!returnSlipId) return;
        dispatch(returnThunks.reopenBatchSlip(returnSlipId)).then((action) => {
            if (returnThunks.reopenBatchSlip.fulfilled.match(action)) {
                navigateToReturnSlip(returnSlipId, ReturnStatusType.InProgress);
            }
        });
    }, [dispatch, returnSlipId, navigateToReturnSlip]);

    const handleApproveBatchHandling = useCallback(() => {
        if (!latestReturnSlip) return;
        dispatch(setBatchHandled(true));
    }, [dispatch, latestReturnSlip]);

    const handleUnapproveBatchHandling = useCallback(() => {
        if (!latestReturnSlip) return;
        dispatch(setBatchHandled(false));
    }, [dispatch, latestReturnSlip]);

    const getUnassignedBatchItemCount = useCallback((): number => {
        if (!latestReturnSlip?.returnItems) return 0;
        let count = 0;
        for (const group of latestReturnSlip.returnItems) {
            for (const line of group.orderLines ?? []) {
                for (const item of line.returnItems ?? []) {
                    if (isBatchAssignable(item) && !item.batchReturnId) {
                        count++;
                    }
                }
            }
        }
        return count;
    }, [latestReturnSlip]);

    return {
        handleSaveWithStatus,
        handleCompleteReturnSlip,
        handleCompleteBatch,
        handleReopenBatchSlip,
        handleApproveBatchHandling,
        handleUnapproveBatchHandling,
        getUnassignedBatchItemCount,
        validateCompleteReturn,
        updateReturnSlip,
        createReturnSlip,
    };
};

export default useReturnSlipActions;
